import { AnimatePresence, motion } from "framer-motion";
import dayjs from "dayjs";
import { useShallow } from "zustand/react/shallow";
import { DAY_LABELS, EASE, MONTH_NAMES, CALENDAR_GRID_VARIANTS } from "@/pages/roadmap/constants";
import { useRoadmapStore } from "@/pages/roadmap/store/roadmap-store";
import { buildCalendarRows, getCellBarsForDate, getCellDate, packEventsForWeek } from "@/pages/roadmap/utils";
import type { DayCell } from "@/pages/roadmap/types";

export function CalendarPane() {
  const maxEventSlots = 5;

  const { viewYear, viewMonth, navDir, shiftView } = useRoadmapStore(
    useShallow((s) => ({
      viewYear: s.viewYear,
      viewMonth: s.viewMonth,
      navDir: s.navDir,
      shiftView: s.shiftView,
    }))
  );

  const { selectedYear, selectedMonth, selectedDay, selectDate } = useRoadmapStore(
    useShallow((s) => ({
      selectedYear: s.selectedYear,
      selectedMonth: s.selectedMonth,
      selectedDay: s.selectedDay,
      selectDate: s.selectDate,
    }))
  );

  const events = useRoadmapStore((s) => s.events);
  const rows = buildCalendarRows(viewYear, viewMonth);

  const isSelectedCell = (cell: DayCell) => {
    const [y, m, d] = getCellDate(cell, viewYear, viewMonth);
    return y === selectedYear && m === selectedMonth && d === selectedDay;
  };

  const isTodayCell = (cell: DayCell) => {
    const [y, m, d] = getCellDate(cell, viewYear, viewMonth);
    const now = dayjs();
    return y === now.year() && m === now.month() && d === now.date();
  };

  const onSelect = (cell: DayCell) => {
    const [y, m, d] = getCellDate(cell, viewYear, viewMonth);
    if (cell.kind === "prev") shiftView(-1);
    if (cell.kind === "next") shiftView(1);
    selectDate(y, m, d);
  };

  return (
    <div className="relative flex items-center justify-center w-full max-w-[648px]">
      <div className="relative flex flex-col w-full max-w-[420px]">
        {/* Desktop Navigation Buttons */}
        <div className="hidden lg:flex absolute inset-y-0 lg:left-[-74px] lg:right-[-74px] lg:top-[60%] items-center justify-between pointer-events-none" style={{ height: "fit-content" }}>
          <button onClick={() => shiftView(-1)} className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#9da1ab] shadow-lg transition-transform hover:scale-110 active:scale-95 cursor-pointer">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button onClick={() => shiftView(1)} className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#9da1ab] shadow-lg transition-transform hover:scale-110 active:scale-95 cursor-pointer">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Header */}
        <div className="mb-6 lg:mb-8 px-2">
          <AnimatePresence mode="wait" custom={navDir}>
            <motion.div key={`title-${viewYear}-${viewMonth}`} custom={navDir} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3, ease: EASE }} className="flex flex-col items-start">
              <div className="text-[24px] lg:text-[38px] font-medium leading-none tracking-tight text-black">{viewYear}</div>
              <div className="mt-2 flex items-center justify-between w-full">
                <div className="text-[40px] lg:text-[50px] font-medium leading-none tracking-tight text-black uppercase">{MONTH_NAMES[viewMonth]}</div>
                <div className="flex lg:hidden items-center gap-1 mt-1">
                  <button onClick={() => shiftView(-1)} className="flex h-7 w-7 items-center justify-center rounded-full bg-[#f0f2f5] active:bg-[#e2e4e7] transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                  <button onClick={() => shiftView(1)} className="flex h-7 w-7 items-center justify-center rounded-full bg-[#f0f2f5] active:bg-[#e2e4e7] transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 18L15 12L9 6" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Calendar Card */}
        <motion.div 
          className="rounded-[24px] bg-white p-6 shadow-[0_25px_70px_rgba(0,0,0,0.12)] border border-[#f0f2f5] touch-pan-y"
          drag="x" dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={(_, info) => {
            const threshold = 50;
            if (info.offset.x < -threshold) shiftView(1);
            else if (info.offset.x > threshold) shiftView(-1);
          }}
        >
          <div className="grid grid-cols-7 mb-4 border-b-[3px] border-[#f3f4f6] pb-3">
            {DAY_LABELS.map((label, index) => (
              <div key={index} className="flex h-8 items-center justify-center text-[18px] font-semibold text-black/60">{label}</div>
            ))}
          </div>

          <AnimatePresence mode="wait" custom={navDir}>
            <motion.div key={`grid-${viewYear}-${viewMonth}`} variants={CALENDAR_GRID_VARIANTS} initial="enter" animate="center" exit="exit" className="flex flex-col gap-1">
              {rows.map((row, rowIndex) => {
                const slots = packEventsForWeek(row, events, viewYear, viewMonth, maxEventSlots);
                return (
                  <div key={rowIndex} className="grid grid-cols-7">
                    {row.map((cell, cellIndex) => {
                      const selected = isSelectedCell(cell);
                      const today = isTodayCell(cell);
                      const isDimmed = cell.kind !== "cur";
                      const [cy, cm, cd] = getCellDate(cell, viewYear, viewMonth);
                      const dailyBars = getCellBarsForDate(events, cy, cm, cd);

                      return (
                        <button key={cellIndex} onClick={() => onSelect(cell)} className="relative flex flex-col items-center pt-1 pb-1 lg:pt-2 lg:pb-2 bg-transparent hover:bg-black/[0.01] transition-colors rounded-lg cursor-pointer">
                          <div
                            className="flex h-8 w-8 items-center justify-center text-[18px] transition-all"
                            style={{
                              background: today ? "#003580" : "transparent",
                              color: today ? "#fff" : selected ? "#003580" : isDimmed ? "#bbb" : "#000",
                              boxShadow: selected && !today ? "inset 0 0 0 2px #003580" : "none",
                              fontWeight: selected || today ? 700 : 500,
                              borderRadius: "50%",
                            }}
                          >
                            {cell.day}
                          </div>
                          
                          <div className="mt-1 flex h-[26px] w-full flex-col gap-[2px]">
                            {slots.map((slotEventIds, i) => {
                              const eventIdInSlot = slotEventIds.find(id => dailyBars.some(b => b.eventId === id));
                              const bar = dailyBars.find(b => b.eventId === eventIdInSlot);
                              if (!bar) return <div key={i} className="h-[3.5px]" />; 

                              const isStart = bar.role === "start" || bar.role === "solo";
                              const isEnd = bar.role === "end" || bar.role === "solo";
                              const isWeekStart = cellIndex === 0;
                              const isWeekEnd = cellIndex === 6;

                              const shouldRoundLeft = isStart || isWeekStart;
                              const shouldRoundRight = isEnd || isWeekEnd;

                              return (
                                <div
                                  key={i}
                                  className="h-[3.5px]"
                                  style={{
                                    backgroundColor: bar.color,
                                    opacity: isDimmed ? 0.3 : 1,
                                    borderRadius: `${shouldRoundLeft ? "100px" : "0"} ${shouldRoundRight ? "100px" : "0"} ${shouldRoundRight ? "100px" : "0"} ${shouldRoundLeft ? "100px" : "0"}`,
                                    marginLeft: shouldRoundLeft ? "4px" : "0",
                                    marginRight: shouldRoundRight ? "4px" : "0",
                                    width: (shouldRoundLeft && shouldRoundRight) ? "calc(100% - 8px)" : (shouldRoundLeft || shouldRoundRight) ? "calc(100% - 4px)" : "100%",
                                  }}
                                />
                              );
                            })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
